<template>
    <div class="h-100 w-100 position-relative spl-file-slinger-upload">
        <div class="h-100 w-100 d-flex justify-content-between">  <!-- CONTENT AREA (with preview) -->
            <div 
                class="d-flex align-items-center justify-content-center"
                :class="showingPreview ? 'w-50' : 'w-100'"
            >
                <div 
                    class="w-100"
                    :class="[small ? 'px-3 py-2' : 'px-4 py-3', showingPreview ? 'w-50' : 'w-100 d-flex flex-wrap gap-2 justify-content-between align-items-center']"
                >
                    <div :class="showingPreview ? '' : 'flex-fill'">
                        <div class="text-break" :class="small ? 'font-weight-500' : 'font-weight-700'">
                            {{filename}}
                            <!-- <span class="text-truncate">{{ filename }}</span> -->
                            <!-- always show filename, no link... show image preview in preview area, or show download link there -->
                            <!-- <span v-if="!isSaved">{{ filename }}</span> -->
                            <!-- <a v-else :href="url" target="_blank">{{ filename }}</a> -->
                        </div>
                        <div v-if="!small" class="small text-muted">{{formatFileSize(filesize)}}</div>
                    </div>
                    <div 
                        class="d-flex gap-2"
                        :class="showingPreview ? 'mt-2 justify-content-start' : 'flex-fill justify-content-end'"
                    >
                        <b-button 
                            v-if="isSaved && url" 
                            variant="gray-primary"
                            :size="small ? 'sm' : 'md'" 
                            :href="url" 
                            target="_blank"
                            class="d-flex align-items-center justify-content-center gap-2"
                        >  <!-- @click="handleDownloadClick" -->
                            <b-icon icon="download" class=""></b-icon>
                            <span v-if="!hideButtonLabels">Download</span>
                        </b-button>
                        <b-button 
                            v-if="canDelete" 
                            key="saved" 
                            :size="small ? 'sm' : 'md'" 
                            variant="gray-danger" 
                            :disabled="loadingDelete"
                            @click="remove"
                        >
                            <b-icon icon="trash"></b-icon>
                        </b-button>
                        <b-button 
                            v-else-if="canCancel" 
                            key="uploading" 
                            :size="small ? 'sm' : 'md'" 
                            variant="primary" 
                            @click="cancelUpload"
                        >
                            Cancel
                        </b-button>
                        <b-button 
                            v-if="!isSaved && !liveUpload" 
                            key="new" 
                            :size="small ? 'sm' : 'md'" 
                            variant="primary" 
                            @click="upload"
                        >
                            Upload
                        </b-button>
                    </div>
                </div>
            </div>
            <div v-if="showingPreview" class="w-50 d-flex align-items-center justify-content-center bg-gray-200 p-2">  
                <!-- <b-progress v-if="isUploading" :value="uploadProgress" :max="100" animated></b-progress> -->    
                <a :href="url" target="_blank" class="h-100 w-100 d-flex align-items-center justify-content-center">
                    <b-img 
                        :src="imagePreviewUrl" 
                        center 
                        blank-color="#eee" 
                        class="shadow-dark rounded-lg animate__animated animate__fast animate__fadeIn" 
                        style="object-fit: contain; max-width: 100%;" 
                        :style="{maxHeight: previewMaxHeight}"
                    ></b-img>
                </a>
            </div>
        </div>
        <transition name="fade-progress-overlay">
            <div 
                v-if="isUploading || showingSuccess" 
                key="progressAndSuccess" 
                class="progress-overlay"
                :class="showingSuccess ? 'is-success' : ''"
                :style="{
                    width: `${uploadProgress}%`,
                }"
            >  <!-- success overlay -->
                <div v-if="showingSuccess" class="success-message text-uppercase font-weight-bold font-size-80 text-center">
                    Upload Complete
                </div>
            </div>
        </transition>
        <!-- <div
            v-if="isUploading" 
            class="upload-progress" 
            :style="{
                width: `${uploadProgress}%`,
            }"
        ></div> -->  <!-- PROGRESS BAR - outside content area -->
    </div>
</template>

<script>
import { 
    FormatFileSizeHelper, 
    slingUpload, 
    getUploadCancelSource,
    deleteS3Upload,
} from './file_slinger_helpers';
import get from 'lodash/get';
import downloadOnDemand from './download_on_demand';


export default {
    props: {
        uid: {  // a local unique ID set in parent list component
            // need this because we'll have some existing, some new files
            // need to be able to track them consistently for deletes, list :key, etc.
            type: [String, Number],
            required: true,
        },
        category: {
            // the S3File category that we use on our backend
            type: String,
            required: true,
        },
        file: {  // if we're uploading a NEW file, this is the native File object
            // type: File,  // not sure how to declare type of File...
            required: false,
        },
        fileData: {  // if this is an EXISTING upload, this is the full object from the API
            // should have { id, download_url, filename, filetype, filesize, file_extension, title, ...maybe more }
            type: Object,
            required: false,
        },
        title: {  // if title is used for this file category
            type: String,
            default: '',
        },
        parentId: {  // if parent_id is required for this file category, and only needed for doing UPLOADS
            type: String,
            default: '',
        },
        liveUpload: {  // if true, we'll start upload IMMEDIATELY when dropping
            type: Boolean,
            default: false,
        },
        disabled: {  // if disabled, we cannot delete!  assume parent will not feed us any files for upload?
            type: Boolean,
            default: false,
        },
        small: {  // if true, makes the list tighter
            type: Boolean,
            default: false,
        },
        noPreview: {  // if true, hides the image preview
            type: Boolean,
            default: false,
        },
        previewMaxHeight: {
            type: String,
            default: '200px',
        },
        previewUrlPath: {
            // the dotted path to the URL to use for src="" for showing preview, if its an image
            // if not an image (based on filetype), this will be ignored
            // set to empty string or null to NOT show any preview
            // should be path 'inside' the S3File object
            // NOTE - this must work for existing images AND response from newly uploaded files
            type: String,
            //default: 'image_urls.thumbnail',
            default: 'download_url',  // 
        },
        hideButtonLabels: {
            type: Boolean,
            default: false, 
        },
    },
    data() {
        return {
            // NO NEED IN HERE -- USE IN PARENT LIST COMPONENT localId: uniqueId(),  // set in created?
            filename: '',  // set in created based on how we're instantiating this = file ? file.name : 'superfile';  // TODO - get filename from backend for existing files
            filetype: '',  // set based on how we're instantiating
            filesize: null,  // set in created based on how we're instantiating this = file ? file.size : 5000;  // TODO - get filesize from backend for existing files  
            id: '',  // if we saved it, this will be set, our backend id
            url: '',  // if already saved, the S3 url
            imagePreviewUrl: '',  // we grab the URL as specified in prop previewUrlPath and set it here
            isUploading: false,  // flag for knowing if currently uploading 
            uploadProgress: 0, // we'll change this as we upload
            axiosCancelSource: null,  // if we're using axios cancel feature, we need 'CancelToken.source()' -- set that here if we get it during upload process
            showingSuccess: false,  
            previewIsHovered: false,  // use v-b-hover="handleHover"
            loadingDelete: false,
        }
    },
    computed: {
        isSaved() {
            return !!this.id;
        },
        isImage() {
            return this.filetype && this.url && this.filetype.startsWith('image');
        },
        showingPreview() {
            return this.isImage && !this.noPreview;  // and saved???
        },
        canCancel() {
            // is the request ongoing, and is it cancellable?  gotta be using axiosCancelSource and be uploading
            // we also check that upload progress is not zero, less than 100 -- so we know the S3 request is ongoing... not the requests to our own backend
            return !!this.isUploading && !!this.axiosCancelSource && (this.uploadProgress > 0) && (this.uploadProgress < 100);
        },
        canDelete() {
            // only if its already uploaded, and we have 'can_delete' in the API fileData
            return !this.disabled && !!this.fileData?.can_delete;
        },
        fileExtension() {
            const parts = this.filename ? this.filename.split('.') : [];
            return parts.length ? parts[(parts.length - 1)] : '';
        },
    },
    created() {
        if (this.file) {
            // if we have this.file, then we're dealing with a NEW file to upload
            this.filename = this.file.name;
            this.filetype = this.file.type;
            this.filesize = this.file.size;
            // do not set id, url
            // HOW TO DEAL WITH OTHER FIELDS
        } else if (this.fileData) {
            // if we have a saved file... set those things
            this.setDataFromApi(this.fileData);
            // OTHER FIELDS?  title, parent_id, etc...???
        } else {
            throw "Must create new FileUpload component with new File or existing fileData";
        }
    },
    mounted() {
        // if we set liveUpload = true, then trigger the upload here
        if (!!this.file && this.liveUpload && !this.isSaved) {
            this.upload();
        }
    },
    methods: {
        formatFileSize(value) {
            return FormatFileSizeHelper(value);
        },
        setDataFromApi(apiData) {
            this.filename = apiData.filename;
            this.filetype = apiData.filetype;
            this.filesize = apiData.filesize;
            this.id = apiData.id;
            this.url = apiData.download_url;
            this.imagePreviewUrl = get(apiData, this.previewUrlPath, '');  // default to empty string if that path doesn't exist
        },
        updateProgress(progressValue) {
            this.uploadProgress = progressValue;
        },
        handleError(err) {
            console.log(`ERROR WHILE UPLOADING ${this.filename}`);
            console.log(err);
            // do more stuff, like emit to parent, etc.
            this.$emit('upload-error', err);  // pass up a message or something
        },
        showSuccess() {
            this.showingSuccess = true;
            setTimeout(() => {
                this.showingSuccess = false;
            }, 2000);
        },
        async upload() {
            // do the upload
            if (this.isSaved) return;  // already saved?  no upload for you...
            if (!this.file) return;
            // OLD - replaced with this.$fileSlinger.getCancelSource() 
            // OLD const cancelSource = getUploadCancelSource();  // then call this.axiosCancelSource.cancel(); if we want to cancel it
            //const cancelSource = this.$fileSlinger.getCancelSource();  // then call this.axiosCancelSource.cancel(); if we want to cancel it
            const cancelSource = getUploadCancelSource();
            this.axiosCancelSource = cancelSource;  // gotta pass to FileSlinger too
            this.isUploading = true;
            const title = this.title || '';
            const parentId = this.parentId;  // a company ID for N, E, Blake
            const progressHandler = this.updateProgress.bind(this);  // need to use .bind() or else it looses this (at least when in a class)
            const errorHandler = this.handleError.bind(this);
            // OLD - replaced with this.$fileSlinger.upload() 
            // OLD const uploadedFileData = await FileSlinger(this.category, this.file, progressHandler, errorHandler, title, parentId, cancelSource);
            //const uploadedFileData = await this.$fileSlinger.upload(this.category, this.file, progressHandler, errorHandler, title, parentId, cancelSource);
            const uploadedFileData = await slingUpload(this.category, this.file, progressHandler, errorHandler, title, parentId, cancelSource);
            // TODO - wrap in try/catch?  or is errorHandler function good enough?
            this.setDataFromApi(uploadedFileData);
            //this.id = id;
            //this.url = url;
            const completedPayload = { uid: this.uid, ...uploadedFileData};  // just let parent handle the raw api response, but with our uid added so it knows which file to deal with
            this.$emit('completed', completedPayload);  
            this.isUploading = false;
            this.showSuccess();
        },
        async cancelUpload() {
            if (this.isSaved) return;
            if (!this.isUploading) return;
            if (!this.axiosCancelSource) return;  // gotta have this to cancel
            this.axiosCancelSource.cancel('Upload has been cancelled');
            this.$emit('cancelled', this.uid);
        },
        async remove() {
            if (!this.canDelete) {
                return;
            }
            this.loadingDelete = true;
            // if (!this.id) return;  // if no id yet (not isSaved) then bail on this
            // OLD - replaced by this.$fileSlinger.delete()
            // OLD const deleteResponse = await deleteS3Upload(this.category, this.id);
            //const deleteResponse = await this.$fileSlinger.delete(this.category, this.id);
            const deleteResponse = await deleteS3Upload(this.category, this.id);
            console.log('FILE WAS DELETED');
            console.log(deleteResponse);
            this.loadingDelete = false;
            this.$emit('deleted', this.uid);
        },
        handleHover(isHovered) {
            this.previewIsHovered = isHovered;
        },
        async handleDownloadClick() {
            // trigger a download on demand
            // NOT USING - seems this only works for same-origin URLs... would have to proxy through our backend
            if (!this.id || !this.category) {
                return;
            }
            const downloadParams = {
                fileCategory: this.category,
                fileId: this.id,
            };
            return await downloadOnDemand(downloadParams);
        },
    }

}
</script>

<style lang="scss">

.spl-file-slinger-upload {
    .upload-progress {
        position: absolute;
        top: 0;
        left: 0;
        background-color: var(--primary-700);
        height: 10px;
        transition: 0.1s ease-in-out;
    }

    .success-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(255,255,255,0.9);
        z-index: 10;
    }

    .progress-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--primary-600);
        opacity: 0.8;
        z-index: 10;
        transition: width 0.1s ease-in-out, background-color 0.1s ease;

        &.is-success {
            background-color: var(--success);
        }

        .success-message {
            color: white;
        }
    }

    /* FADE - <transition name="fade"> */
    .fade-progress-overlay-enter-active, .fade-progress-overlay-leave-active {
        transition: opacity 0.3s;
    }
    .fade-progress-overlay-enter, .fade-progress-overlay-leave-to {
        opacity: 0;
    }
}

</style>
